/**
 * Note schemas — Tier 1 internal docs / wiki / per-client narrative summary.
 *
 * Notes can be scoped to a client, engagement, or compliance item. Rich-text
 * stored as markdown. Mention support (@user) parses on the server.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, pageInput } from './common';

export const noteScopeSchema = z.enum([
  'CLIENT',
  'ENGAGEMENT',
  'COMPLIANCE_CLIENT',
  'COMPLIANCE_ITEM',
  'WORKSPACE',
]);

export const noteOutputSchema = z.object({
  id: idSchema,
  scope: noteScopeSchema,
  /** ID of the entity this note is scoped to (matches scope). */
  scopeId: idSchema.nullable(),
  title: z.string().min(1).max(200).nullable(),
  bodyMarkdown: z.string().max(50000),
  authorId: idSchema,
  pinned: z.boolean().default(false),
  /** OPS 3.8 step 1: optional link to a checklist row on the scoped engagement
   *  (scope ENGAGEMENT only). The title is server-derived from the live row. */
  engagementSubItemId: idSchema.nullable(),
  engagementSubItemTitle: z.string().max(200).nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type NoteOutput = z.infer<typeof noteOutputSchema>;

const createNoteObject = z.object({
  scope: noteScopeSchema,
  scopeId: idSchema.nullable().optional(),
  title: z.string().min(1).max(200).nullable().optional(),
  bodyMarkdown: z.string().min(1).max(50000),
  pinned: z.boolean().default(false),
  /** Link to a checklist row — ENGAGEMENT scope + scopeId required; the server
   *  validates parent match, liveness, row scope and engagements.edit. */
  engagementSubItemId: idSchema.nullable().optional(),
});
export const createNoteInputSchema = createNoteObject.superRefine((input, context) => {
  if (input.scope === 'WORKSPACE' ? !!input.scopeId || !!input.engagementSubItemId : !input.scopeId) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['scopeId'], message: 'Business notes require a parent ID; workspace notes cannot have one' });
  }
  if (input.engagementSubItemId && input.scope !== 'ENGAGEMENT') context.addIssue({ code: z.ZodIssueCode.custom, path: ['engagementSubItemId'], message: 'Checklist links require engagement scope' });
});
export type CreateNoteInput = z.infer<typeof createNoteInputSchema>;

/** Context (scope/scopeId/sub-item link) is immutable — only content edits. */
export const updateNoteInputSchema = createNoteObject.partial().omit({
  scope: true,
  scopeId: true,
  engagementSubItemId: true,
});
export type UpdateNoteInput = z.infer<typeof updateNoteInputSchema>;

export const listNotesInputSchema = pageInput.extend({
  scope: noteScopeSchema.optional(),
  scopeId: idSchema.optional(),
  pinned: z.boolean().optional(),
});
export type ListNotesInput = z.infer<typeof listNotesInputSchema>;